// Zod schemas for every route's `validateSearch`. TanStack Router (v1.166+)
// accepts a Standard Schema validator directly — no @tanstack/zod-adapter
// needed for zod v4. See https://tanstack.com/router/latest/docs/framework/react/guide/search-params
//
// Every optional field uses `.catch(...)` so a malformed/tampered query string
// (wrong type, stale enum value, garbage string) falls back to `undefined` or
// a safe default instead of throwing and breaking navigation.
import { z } from "zod";
import type { ComparisonMode } from "./dateUtils";
import type { Grouping } from "@aco/core";
import type { Platform } from "@aco/core";

const PLATFORM_FILTER_VALUES = [
  "all",
  "meta",
  "google",
  "tiktok",
  "snapchat",
] as const satisfies readonly ("all" | Platform)[];
const GROUPING_VALUES = ["day", "week", "month"] as const satisfies readonly Grouping[];
const COMPARISON_MODE_VALUES = [
  "period",
  "wow",
  "mom",
  "none",
] as const satisfies readonly ComparisonMode[];

// How DataGrid renders period-over-period deltas. `undefined` (param absent)
// means the default: hidden — deltas are opt-in to keep first read calm.
const DELTA_DISPLAY_VALUES = ["percent", "absolute", "hidden"] as const;
export type DeltaDisplay = (typeof DELTA_DISPLAY_VALUES)[number];

// The comparison query is the same size as the current-period query. Do not
// fetch it while the UI is explicitly hiding deltas; opt back in only when a
// percentage or absolute comparison is visible.
export function effectiveComparisonMode(
  deltas: DeltaDisplay | undefined,
  comparisonMode: ComparisonMode | undefined,
): ComparisonMode {
  if (!deltas || deltas === "hidden" || comparisonMode === "none") return "none";
  return comparisonMode ?? "period";
}

// The baseline a preset compares against. A calendar week is read against the
// same weekdays of the week before, a calendar month against the month before;
// a rolling or custom range against the days immediately preceding it. "This
// week" on a Saturday would otherwise compare Mon–Fri with the previous
// Tue–Sat, which reads as noise.
export function presetComparisonMode(preset: string | undefined): ComparisonMode {
  if (preset === "thisWeek" || preset === "lastWeek") return "wow";
  if (preset === "thisMonth" || preset === "lastMonth") return "mom";
  return "period";
}

// Empty-string params (e.g. `?start=`) mean "unset", same as an omitted key —
// collapse both to `undefined` rather than round-tripping the empty string.
const optionalDateString = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);

const optionalNonEmptyString = z.string().min(1).optional().catch(undefined);
const optionalIdString = z
  .preprocess((value) => {
    if (typeof value !== "string") return value;
    // TanStack's default search serializer JSON-quotes numeric-looking string
    // IDs. Normalize those links before matching them against provider IDs.
    if (value.startsWith('"') && value.endsWith('"')) {
      try {
        const parsed = JSON.parse(value);
        return typeof parsed === "string" ? parsed : value;
      } catch {
        return value;
      }
    }
    return value;
  }, z.string().min(1).optional())
  .catch(undefined);

// A Google customer id in a link. TanStack's default parser JSON-parses
// `account=9227396956` into a number, and its serializer JSON-quotes it.
const googleCustomerIdSchema = z
  .preprocess(
    (value) => (typeof value === "number" && Number.isSafeInteger(value) ? String(value) : value),
    optionalIdString.pipe(
      z
        .string()
        .regex(/^\d{10}$/)
        .optional(),
    ),
  )
  .catch(undefined);

const platformFilterSchema = z.enum(PLATFORM_FILTER_VALUES).optional().catch(undefined);
const groupingSchema = z.enum(GROUPING_VALUES).optional().catch(undefined);
const comparisonModeSchema = z.enum(COMPARISON_MODE_VALUES).optional().catch(undefined);
const deltaDisplaySchema = z.enum(DELTA_DISPLAY_VALUES).optional().catch(undefined);
const ANALYTICS_VIEW_VALUES = ["overview", "campaign", "creative"] as const;
export type AnalyticsView = (typeof ANALYTICS_VIEW_VALUES)[number];
const analyticsViewSchema = z.enum(ANALYTICS_VIEW_VALUES).optional().catch(undefined);

// /$orgSlug — the dashboard toolbar's date range, preset, platform
// filter, grouping, and comparison mode. Child routes (index) read this
// validated search instead of re-casting the same fields.
export const orgSearchSchema = z.object({
  start: optionalDateString,
  end: optionalDateString,
  preset: optionalNonEmptyString,
  platform: platformFilterSchema,
  grouping: groupingSchema,
  comparisonMode: comparisonModeSchema,
  deltas: deltaDisplaySchema,
  view: analyticsViewSchema,
  segment: optionalNonEmptyString,
  breakdown: z
    .union([z.uuid(), z.array(z.uuid()).max(100)])
    .transform((value) => [...new Set(typeof value === "string" ? [value] : value)])
    .optional()
    .catch(undefined),
  campaign: optionalNonEmptyString,
  // The selected creative's stable identity (`provider:account:entityId`). It
  // opens the same-page By creative detail state and, being in the URL, makes
  // the selection shareable and restorable through browser navigation.
  creative: optionalNonEmptyString,
});
export type OrgSearch = z.infer<typeof orgSearchSchema>;

// /$orgSlug/settings
// The Company's own sections — conversions, manual-data, segments, display,
// attribution, extra-metrics, audit-context, budgets, members, connections — the sections that span all
// Companies (formerly Global admin): companies, people, logins, launch-ops — and
// the person's own: connected-apps. The ordinary Organisation tab was removed; a stale
// `?tab=organization` link falls back to the default below rather than showing
// a dead section. A section the person may not open falls back to their first.
const SETTINGS_TAB_VALUES = [
  "conversions",
  "manual-data",
  "segments",
  "display",
  "attribution",
  "extra-metrics",
  "audit-context",
  "budgets",
  "members",
  "connections",
  "connected-apps",
  "companies",
  "people",
  "logins",
  "launch-ops",
] as const;
// The Company profile became part of Business context.
const settingsTabSchema = z
  .preprocess(
    (value) => (value === "profile" ? "audit-context" : value),
    z.enum(SETTINGS_TAB_VALUES),
  )
  .catch("conversions");
export type SettingsTab = z.infer<typeof settingsTabSchema>;

export const settingsSearchSchema = z.object({
  tab: settingsTabSchema,
  // The Google account the Audit context tab opens on (the desktop app links
  // here from an account's Settings); an unknown id falls back to the first.
  account: googleCustomerIdSchema,
});
export type SettingsSearch = z.infer<typeof settingsSearchSchema>;

// /$orgSlug/audit and its tabs — the Google account the page shows; absent or
// unknown falls back to the one last chosen here, then the first.
export const auditSearchSchema = z.object({ account: googleCustomerIdSchema });
export type AuditSearch = z.infer<typeof auditSearchSchema>;

// /$orgSlug/launch/create — one continuous Create workspace with a platform view.
// `sheet` remains a compatibility deep-link input while the Create storage
// migration is coordinated; new navigation only exposes `platform` to users.
const launchCreatePlatformSchema = z.enum(["meta", "tiktok"]).optional().catch(undefined);

export const launchCreateSearchSchema = z.object({
  sheet: optionalNonEmptyString,
  platform: launchCreatePlatformSchema,
  // "rejected": arrive with the sheet's rejected rows selected (the launch
  // progress bar's link from a finished job).
  show: z.enum(["rejected"]).optional().catch(undefined),
  // A chat's Preflight link: the rows to open Preflight on, comma-separated.
  preflight: optionalNonEmptyString,
  // A chat's link to let Growth Engine read the person's Google Drive.
  drive: z.enum(["allow"]).optional().catch(undefined),
});
export type LaunchCreateSearch = z.infer<typeof launchCreateSearchSchema>;

// /$orgSlug/launch/edit — the ladder lives in the URL so a link to an ad set
// can be shared and Back walks up it. Each rung is only read when the one above
// it is present (see edit-place.ts). Provider IDs are numeric-looking, so they
// go through optionalIdString's JSON-quote normalisation.
export const launchEditSearchSchema = z.object({
  platform: launchCreatePlatformSchema,
  account: optionalIdString,
  campaign: optionalIdString,
  adGroup: optionalIdString,
});
export type LaunchEditSearch = z.infer<typeof launchEditSearchSchema>;
